import {
  inject,
  Injectable,
  resource,
  ResourceRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  IOnDemandBookingConfirmationRequest,
  IOnDemandBookingRequest,
  IOnDemandBookingResponse,
  MILLISECONDS_PER_MINUTE,
  OperatorMappingId,
  RESERVATION_DURATION_MINUTES,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { OnDemandAvailabilityGateway } from '../api/on-demand/on-demand-availability.gateway';
import { OnDemandSelectionStateService } from './on-demand-selection-state.service';
import { OnDemandAvailabilityStore } from '../api/on-demand/on-demand-availability-store.service';
import { LoggingService } from '../logging/logging.service';
import { UserStore } from '../api/user/user/user-store.service';

@Injectable({ providedIn: 'root' })
export class OnDemandBookingStore {
  private readonly gateway: OnDemandAvailabilityGateway =
    new OnDemandAvailabilityGateway();
  private readonly selectionState: OnDemandSelectionStateService = inject(
    OnDemandSelectionStateService
  );
  private readonly availabilityStore: OnDemandAvailabilityStore = inject(
    OnDemandAvailabilityStore
  );
  private readonly userStore: UserStore = inject(UserStore);

  constructor(private readonly log: LoggingService) {}

  private readonly reserveTrigger: WritableSignal<
    IOnDemandBookingRequest | undefined
  > = signal(undefined);

  private readonly reserveResource: ResourceRef<
    IApiResponse<IOnDemandBookingResponse> | undefined
  > = resource({
    params: this.reserveTrigger,
    loader: async ({ params }) => {
      if (!params) return undefined;

      const response = await firstValueFrom(this.gateway.reserve(params));

      if (response?.data) {
        this.selectionState.reservationBookingResponse.set(response.data);

        const expires = new Date(
          Date.now() + RESERVATION_DURATION_MINUTES * MILLISECONDS_PER_MINUTE
        );
        this.selectionState.reservationExpiresAt.set(expires.toISOString());
      }
      return response;
    },
  });

  triggerReservation(
    passengerCount: number,
    customerName: string,
    customerEmail: string,
    customerPhone?: string
  ): void {
    const operatorMappingId: OperatorMappingId | null =
      this.availabilityStore.matchedMapping()?.id ??
      this.selectionState.checkoutOperatorMapping()?.id ??
      this.selectionState.reservationOperatorMappingId();
    const option = this.selectionState.selectedConnectionOption();

    if (!operatorMappingId || !option?.departureTimetabled) {
      this.log.warn(
        'OnDemandBookingStore: Missing operator mapping or connection option.'
      );
      return;
    }

    this.selectionState.reservationOperatorMappingId.set(operatorMappingId);
    const appCode =
      this.selectionState.bookingAppCode() ||
      this.userStore.userOrGuestId() ||
      crypto.randomUUID();
    this.selectionState.bookingAppCode.set(appCode);
    this.reserveTrigger.set({
      operatorMappingId,
      appCode,
      connectionInformation: option.connectionInformation,
      earliestDeparture: option.departureTimetabled,
      latestArrival: option.arrivalTimetabled,
      passengerCount,
      isReservation: true,
      extrasPerPassenger: this.selectionState.extrasPerPassenger(),
      customerName,
      customerEmail,
      customerPhone,
    });
  }

  getConfirmationRequest(
    customerName: string,
    customerEmail: string,
    customerPhone?: string
  ): IOnDemandBookingConfirmationRequest {
    const operatorMappingId: OperatorMappingId | null =
      this.availabilityStore.matchedMapping()?.id ??
      this.selectionState.reservationOperatorMappingId();
    const reservation =
      this.selectionState.reservationBookingResponse() ??
      this.reserveResource.value()?.data;
    const appCode = this.selectionState.bookingAppCode();

    if (!operatorMappingId || !reservation || !appCode) {
      throw new Error('No on-demand reservation is available to confirm.');
    }

    return {
      operatorMappingId,
      appCode,
      systemRef: reservation.systemRef,
      bookingCode: reservation.bookingRef,
      customerName,
      customerEmail,
      customerPhone,
    };
  }
}
